import { Button, Alert, IconButton, InputAdornment, Select, MenuItem, Typography, FormControl, InputLabel, Divider } from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Input from "../components/Input";
import { useAPI } from "../services/API";
import { UserContext } from "../services/UserContext";
import { Visibility, VisibilityOff, LocalFlorist } from '@mui/icons-material';

const GRADIENT = "linear-gradient(150deg, #27264A 0%, #5957a8 100%)";

export default function Login() {
    const [loginData, setLoginData] = useState({ username: undefined, password: undefined });
    const [isOk, setIsOk] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [communes, setCommunes] = useState(null);
    const [selectedCommune, setSelectedCommune] = useState("");

    const [, setUserContext] = useContext(UserContext);
    const api = useAPI();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname;

    useEffect(() => {
        setIsOk(Object.values(loginData).every(v => v !== undefined));
    }, [loginData]);

    const handleChange = (key, value) => {
        setLoginData(curr => ({ ...curr, [key]: value }));
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError("");

        // Vider les cookies stale avant login — utilise <img> pour contourner CORS
        // (les requêtes image ne sont pas soumises à CORS, mais Set-Cookie est quand même appliqué)
        await new Promise((resolve) => {
            const img = new Image();
            img.onload = img.onerror = resolve;
            img.src = `${process.env.REACT_APP_API_URL}/utilisateur/clear-cookies?_t=${Date.now()}`;
            setTimeout(resolve, 3000);
        });

        try {
            const res = await api("POST", "utilisateur/login", loginData, {}, "application/json", { credentials: "include" });
            setIsSubmitting(false);

            if (!res.ok) {
                setError(res.status === 401 ? "Identifiant ou mot de passe incorrect" : "Une erreur est survenue. Veuillez réessayer");
                return;
            }

            setUserContext(res.json);
            const { role, commune } = res.json;

            let communeList;
            if (role === 'super') {
                const communesRes = await api("GET", "communes", '', {}, "application/json", {}, res.json.token);
                communeList = communesRes.ok ? communesRes.json : [];
            } else {
                communeList = commune ? [commune] : [];
            }

            if (communeList.length === 0) {
                setError("Aucune commune accessible pour cet utilisateur");
                return;
            }

            if (communeList.length === 1) {
                navigate(from || `/${communeList[0]}/`, { replace: true });
            } else {
                setCommunes(communeList);
            }
        } catch {
            setIsSubmitting(false);
            setError("Une erreur est survenue. Veuillez réessayer");
        }
    };

    if (communes) {
        return (
            <PageLayout>
                <BrandPanel />
                <FormPanel>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#27264A", mb: 0.5 }}>
                        Choisissez une commune
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Votre compte a accès à plusieurs communes
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Commune</InputLabel>
                        <Select value={selectedCommune} label="Commune" onChange={e => setSelectedCommune(e.target.value)}>
                            {communes.map(c => (
                                <MenuItem key={c} value={c}>
                                    {c.charAt(0).toUpperCase() + c.slice(1)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained" fullWidth size="large"
                        disabled={!selectedCommune}
                        sx={{ borderRadius: 2, py: 1.5, textTransform: "none", fontSize: "1rem" }}
                        onClick={() => navigate(`/${selectedCommune}/`, { replace: true })}
                    >
                        Accéder
                    </Button>
                </FormPanel>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <BrandPanel />
            <FormPanel>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#27264A", mb: 0.5 }}>
                    Connexion
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Accédez à votre espace de gestion
                </Typography>

                {error !== "" && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

                <form onSubmit={handleLogin}>
                    <Input
                        name="Identifiant"
                        required
                        onChange={val => handleChange("username", val)}
                        otherProps={{ fullWidth: true }}
                    />
                    <Input
                        name="Mot de passe"
                        required
                        onChange={val => handleChange("password", val)}
                        style={{ marginTop: 16 }}
                        otherProps={{ fullWidth: true, type: showPassword ? 'text' : 'password' }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(p => !p)} edge="end">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size="large"
                        disabled={!isOk || isSubmitting}
                        sx={{ mt: 3, borderRadius: 2, py: 1.5, textTransform: "none", fontSize: "1rem" }}
                    >
                        {isSubmitting ? "Identification en cours…" : "S'identifier"}
                    </Button>
                </form>
            </FormPanel>
        </PageLayout>
    );
}

function PageLayout({ children }) {
    return (
        <Box sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
        }}>
            {children}
        </Box>
    );
}

function BrandPanel() {
    return (
        <Box sx={{
            flex: { xs: "0 0 auto", md: "0 0 42%" },
            background: GRADIENT,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: { xs: 4, md: 6 },
            color: "white",
            minHeight: { xs: 200, md: "100vh" },
        }}>
            <LocalFlorist sx={{ fontSize: { xs: 48, md: 72 }, mb: 2, opacity: 0.85 }} />
            <Typography
                variant="h3"
                sx={{ fontWeight: 800, letterSpacing: 2, mb: 1, fontSize: { xs: "2rem", md: "2.8rem" } }}
            >
                Sérenicim
            </Typography>
            <Divider sx={{ width: 56, borderColor: "rgba(255,255,255,0.35)", my: 2 }} />
            <Typography
                variant="body1"
                sx={{ textAlign: "center", opacity: 0.75, maxWidth: 280, lineHeight: 1.8, fontSize: "1rem" }}
            >
                La gestion de cimetière<br />simple et sereine
            </Typography>
        </Box>
    );
}

function FormPanel({ children }) {
    return (
        <Box sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#F6F6F9",
            p: { xs: 3, md: 6 },
        }}>
            <Box sx={{
                width: "100%",
                maxWidth: 420,
                backgroundColor: "white",
                borderRadius: 3,
                p: { xs: 3, md: 4 },
                boxShadow: "0 4px 32px rgba(39,38,74,0.09)",
            }}>
                {children}
            </Box>
        </Box>
    );
}
